import { useState, useEffect, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, Loader2, CheckCircle2, AlertCircle, Package2, Barcode,
  MapPin, Clock, RotateCcw, FastForward, PackageCheck, Warehouse,
  X as XIcon, PlusCircle, MinusCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

interface SepItem {
  sep_id: number
  produto_id: number
  produto_codigo: string
  produto_ean: string | null
  produto_descricao: string
  unidade: string
  saida_por: string
  endereco: string | null
  lote_id: number | null
  lote_codigo: string | null
  validade: string | null
  quantidade_prevista: number
  quantidade_separada: number
  concluido: boolean
  ultima_bipagem_em: string | null
}
interface MapaSeparacao {
  pedido_id: number
  pedido_numero: number
  pedido_status: string
  deposito_id: number
  total_previsto: number
  total_separado: number
  progresso_pct: number
  itens: SepItem[]
}

// =============================================================
// PAGE ROOT
// =============================================================
export default function SeparacaoPage() {
  const { id } = useParams<{ id: string }>()
  const nav = useNavigate()
  const qc = useQueryClient()
  const pid = Number(id)

  const [quantidade, setQuantidade] = useState(1)
  const inputRef = useRef<HTMLInputElement>(null)
  const [ultimoBip, setUltimoBip] = useState<{
    produto: string; qtd: number; ok: boolean; msg: string
  } | null>(null)

  // Chama gerar-mapa que é idempotente: retorna existente se já tem, senão cria
  const { data: mapa, isLoading, error } = useQuery<MapaSeparacao>({
    queryKey: ['mapa-separacao', pid],
    queryFn: async () => (await api.post(`/vendas/pedidos/${pid}/gerar-mapa-separacao`)).data,
    retry: false,
  })

  // Autofoco sempre que a tela carrega
  useEffect(() => {
    if (mapa) inputRef.current?.focus()
  }, [mapa])

  // Mutation de bipagem
  const bipar = useMutation({
    mutationFn: async ({ codigo, qtd, forcar }: { codigo: string; qtd: number; forcar?: boolean }) =>
      (await api.post(`/vendas/pedidos/${pid}/bipagem`, {
        codigo, quantidade: qtd, forcar_excesso: !!forcar,
      })).data,
    onSuccess: (r) => {
      setUltimoBip({
        produto: r.produto_descricao,
        qtd: r.quantidade_separada,
        ok: true,
        msg: r.concluido_item
          ? `${r.produto_descricao} COMPLETO (${r.quantidade_separada}/${r.quantidade_prevista})`
          : `${r.produto_descricao}: ${r.quantidade_separada}/${r.quantidade_prevista}`,
      })
      qc.invalidateQueries({ queryKey: ['mapa-separacao', pid] })
      if (r.tudo_separado) {
        toast.success('🎉 Todos os itens separados!', { duration: 4000 })
      }
      // Beep audio visual
      try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain); gain.connect(ctx.destination)
        osc.frequency.value = r.concluido_item ? 880 : 660
        gain.gain.setValueAtTime(0.1, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15)
        osc.start(); osc.stop(ctx.currentTime + 0.15)
      } catch {}
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.detail || 'Erro na bipagem'
      setUltimoBip({ produto: '', qtd: 0, ok: false, msg })
      // Se for excesso, pergunta se quer forcar
      if (msg.includes('excede quantidade')) {
        if (confirm(`${msg}\n\nForçar bipagem mesmo assim?`)) {
          const codigo = (document.querySelector<HTMLInputElement>('#bip-input')?.value || '')
          if (codigo) bipar.mutate({ codigo, qtd: quantidade, forcar: true })
        }
      }
      // Beep de erro
      try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain); gain.connect(ctx.destination)
        osc.frequency.value = 200
        gain.gain.setValueAtTime(0.15, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3)
        osc.start(); osc.stop(ctx.currentTime + 0.3)
      } catch {}
    },
  })

  const desfazer = useMutation({
    mutationFn: async (sep_id: number) =>
      (await api.post(`/vendas/pedidos/${pid}/desfazer-bipagem/${sep_id}`)).data,
    onSuccess: () => {
      toast.success('Item zerado')
      qc.invalidateQueries({ queryKey: ['mapa-separacao', pid] })
    },
  })

  const concluir = useMutation({
    mutationFn: async () => (await api.post(`/vendas/pedidos/${pid}/concluir-separacao`, {})).data,
    onSuccess: () => {
      toast.success('Separação concluída → Embalagem')
      qc.invalidateQueries({ queryKey: ['pedidos'] })
      qc.invalidateQueries({ queryKey: ['pedido', pid] })
      nav('/vendas')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const pausar = useMutation({
    mutationFn: async (obs: string) =>
      (await api.post(`/vendas/pedidos/${pid}/pausar-separacao`, { observacao: obs })).data,
    onSuccess: () => {
      toast.success('Separação pausada')
      qc.invalidateQueries({ queryKey: ['pedidos'] })
      qc.invalidateQueries({ queryKey: ['pedido', pid] })
      nav('/vendas')
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const input = inputRef.current
    if (!input || !input.value.trim()) return
    const codigo = input.value.trim()
    bipar.mutate({ codigo, qtd: quantidade })
    input.value = ''
    setQuantidade(1)  // volta para 1 apos cada bip
    input.focus()
  }

  // Stats do mapa
  const stats = useMemo(() => {
    if (!mapa) return { itensCompletos: 0, itensAbertos: 0 }
    const completos = mapa.itens.filter(i => i.concluido).length
    return {
      itensCompletos: completos,
      itensAbertos: mapa.itens.length - completos,
    }
  }, [mapa])

  if (error) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <AlertCircle className="inline mr-2" /> Erro: {String((error as any)?.message || error)}
        </div>
        <button onClick={() => nav('/vendas')} className="btn-ghost mt-3">
          <ArrowLeft size={14} /> Voltar
        </button>
      </div>
    )
  }

  if (isLoading || !mapa) {
    return (
      <div className="flex items-center justify-center h-96 text-ink-400">
        <Loader2 className="animate-spin mr-2" /> Carregando mapa de separação...
      </div>
    )
  }

  const todosConcluidos = mapa.total_separado >= mapa.total_previsto

  return (
    <div className="min-h-screen bg-ink-50">
      {/* HEADER FIXO */}
      <div className="bg-ink-900 text-white px-6 py-3 flex items-center justify-between sticky top-0 z-40 shadow-lg">
        <div className="flex items-center gap-3">
          <button onClick={() => nav('/vendas')}
            className="p-2 rounded hover:bg-ink-800">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="text-xs text-ink-400 uppercase tracking-wider">Separação</div>
            <div className="font-semibold">Pedido #{mapa.pedido_numero}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => {
            const obs = prompt('Motivo da pausa (opcional):') || ''
            pausar.mutate(obs)
          }}
            className="text-xs px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700">
            ⏸ Pausar
          </button>
          <button
            disabled={!todosConcluidos || concluir.isPending}
            onClick={() => concluir.mutate()}
            className="text-sm px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed font-medium flex items-center gap-1.5">
            {concluir.isPending
              ? <><Loader2 size={14} className="animate-spin" /> Concluindo...</>
              : <><PackageCheck size={14} /> Concluir separação</>
            }
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-4">

        {/* BARRA DE PROGRESSO GIGANTE */}
        <div className="bg-white rounded-xl shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-ink-600">Progresso</div>
            <div className="text-sm">
              <span className="font-semibold">{mapa.total_separado}</span>
              <span className="text-ink-400"> / {mapa.total_previsto}</span>
              <span className="ml-2 text-xs text-ink-500">
                ({stats.itensCompletos}/{mapa.itens.length} itens)
              </span>
            </div>
          </div>
          <div className="h-4 bg-ink-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                todosConcluidos ? 'bg-emerald-500' : 'bg-brand-500'
              }`}
              style={{ width: `${Math.min(100, mapa.progresso_pct)}%` }}
            />
          </div>
        </div>

        {/* INPUT DE BIPAGEM (GRANDE E VISIVEL) */}
        {!todosConcluidos && (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-ink-600">
              <Barcode size={18} />
              <label className="text-sm font-medium">
                Bipe o código de barras ou digite o SKU/EAN
              </label>
            </div>
            <div className="flex gap-2">
              <input
                id="bip-input"
                ref={inputRef}
                type="text"
                autoComplete="off"
                autoFocus
                placeholder="Escaneie aqui..."
                className="flex-1 px-5 py-4 text-xl font-mono bg-ink-50 border-2 border-ink-200 rounded-lg focus:border-brand-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-brand-100"
              />
              <div className="flex items-center gap-1 bg-ink-50 rounded-lg border-2 border-ink-200 px-1">
                <button type="button"
                  onClick={() => setQuantidade(q => Math.max(1, q - 1))}
                  className="p-2 hover:bg-ink-100 rounded">
                  <MinusCircle size={18} className="text-ink-500" />
                </button>
                <input type="number" min={1} value={quantidade}
                  onChange={e => setQuantidade(parseInt(e.target.value) || 1)}
                  className="w-16 text-center text-xl font-semibold bg-transparent outline-none" />
                <button type="button"
                  onClick={() => setQuantidade(q => q + 1)}
                  className="p-2 hover:bg-ink-100 rounded">
                  <PlusCircle size={18} className="text-ink-500" />
                </button>
              </div>
              <button type="submit" disabled={bipar.isPending}
                className="px-6 py-4 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-medium disabled:opacity-50">
                {bipar.isPending
                  ? <Loader2 size={18} className="animate-spin" />
                  : <><FastForward size={18} className="inline" /> Bipar</>
                }
              </button>
            </div>
            <p className="text-xs text-ink-500">
              💡 Quantidade volta para 1 depois de cada bip. Use os botões + / − ou digite um número maior para bipar vários de uma vez.
            </p>
          </form>
        )}

        {/* FEEDBACK DO ULTIMO BIP */}
        {ultimoBip && (
          <div className={`rounded-xl p-3 flex items-center gap-3 text-sm ${
            ultimoBip.ok
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
              : 'bg-red-50 border border-red-200 text-red-900'
          }`}>
            {ultimoBip.ok ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
            <span className="font-medium">{ultimoBip.msg}</span>
            <button onClick={() => setUltimoBip(null)}
              className="ml-auto p-1 hover:bg-white/50 rounded">
              <XIcon size={14} />
            </button>
          </div>
        )}

        {/* LISTA DE ITENS */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="px-4 py-3 border-b border-ink-100 text-xs font-semibold text-ink-600 uppercase tracking-wide flex items-center gap-2">
            <Package2 size={14} /> Itens a separar
          </div>
          <div className="divide-y divide-ink-100">
            {mapa.itens.map(it => (
              <div key={it.sep_id}
                className={`px-4 py-3 flex items-center gap-3 ${
                  it.concluido ? 'bg-emerald-50/30' : ''
                }`}>
                {/* Indicador */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  it.concluido ? 'bg-emerald-100 text-emerald-700' : 'bg-ink-100 text-ink-500'
                }`}>
                  {it.concluido ? <CheckCircle2 size={20} /> : <Package2 size={20} />}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-ink-900">{it.produto_descricao}</span>
                    <span className="text-xs text-ink-400 font-mono">#{it.produto_codigo}</span>
                    {it.produto_ean && (
                      <span className="text-xs text-ink-400 font-mono">· EAN {it.produto_ean}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-ink-500 mt-0.5 flex-wrap">
                    {it.endereco && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={11} /> <strong>{it.endereco}</strong>
                      </span>
                    )}
                    {it.lote_codigo && (
                      <span>Lote <strong>{it.lote_codigo}</strong></span>
                    )}
                    {it.validade && (
                      <span className="text-amber-700">Val: {new Date(it.validade).toLocaleDateString('pt-BR')}</span>
                    )}
                    <span className={`px-1.5 py-0.5 rounded font-medium text-[10px] ${
                      it.saida_por === 'FEFO' ? 'bg-amber-100 text-amber-700'
                      : it.saida_por === 'MANUAL' ? 'bg-purple-100 text-purple-700'
                      : 'bg-blue-100 text-blue-700'
                    }`}>
                      {it.saida_por}
                    </span>
                    {it.ultima_bipagem_em && (
                      <span className="inline-flex items-center gap-1">
                        <Clock size={10} /> {new Date(it.ultima_bipagem_em).toLocaleTimeString('pt-BR')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Contador grande */}
                <div className={`text-right px-3 ${
                  it.concluido ? 'text-emerald-600' : 'text-ink-900'
                }`}>
                  <div className="text-2xl font-bold leading-none">
                    {it.quantidade_separada}
                    <span className="text-ink-300 text-base">/{it.quantidade_prevista}</span>
                  </div>
                  <div className="text-[10px] text-ink-400 uppercase mt-0.5">{it.unidade}</div>
                </div>

                {/* Desfazer */}
                {it.quantidade_separada > 0 && (
                  <button onClick={() => {
                    if (confirm(`Zerar bipagem de ${it.produto_descricao}?`))
                      desfazer.mutate(it.sep_id)
                  }}
                    className="p-2 rounded hover:bg-red-50 text-red-600"
                    title="Zerar">
                    <RotateCcw size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {todosConcluidos && (
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-5 text-center">
            <CheckCircle2 size={40} className="mx-auto text-emerald-600 mb-2" />
            <h3 className="font-semibold text-emerald-900">Tudo separado! 🎉</h3>
            <p className="text-sm text-emerald-700 mt-1">
              Clique em "Concluir separação" acima para avançar para embalagem.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
